import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye } from '../components/Icons.jsx';

const videos = [
  { id: 'fFcCrVsyvCw', section: 'VR', title: '15 VR tips to score highly', desc: 'High-level tips for Verbal Reasoning — 15 specific moves that lift your score.' },
  { id: 'ivX3QoAwJXk', section: 'VR', title: 'True / False / Can\'t Tell', desc: 'Decision rules for the T/F/CT question type — the most common VR shape.' },
  { id: 'WJyzklD5Y38', section: 'VR', title: 'Using inferences', desc: 'How to draw safe inferences from passages without overstepping.' },
  { id: '-T4n7OBj8tw', section: 'VR', title: 'Extreme language traps', desc: 'Words like "always" and "never" — when they mark the answer wrong by design.' },
  { id: 'MVAK10d7Ei0', section: 'VR', title: 'Passage adjustments', desc: 'How to adjust your reading strategy when the passage shape changes.' },
  { id: 'IgUwQTWG2-c', section: 'VR', title: 'Keyword approach', desc: 'Scan-and-retrieve technique using keywords — the highest-yield VR habit.' },
  { id: 'GHWMpIHWkmA', section: 'VR', title: 'Writer questions', desc: 'The "what would the writer think?" shape — pattern recognition for inferring tone.' },
  { id: 'Kgx3fEVus5E', section: 'DM', title: 'Syllogisms', desc: 'Two-statement logic — how to draw mini Venn diagrams in 8 seconds.' },
  { id: 'PsRkOPqkmWI', section: 'SJT', title: 'SJT scenarios — tips', desc: 'How to approach Situational Judgement questions for partial credit.' },
  { id: 'PgqPXy97yNk', section: 'SJT', title: 'Approaching the scenario', desc: 'Read the patient, the role and the consequence — not just the action.' },
  { id: 'AF3zZasn5Ok', section: 'SJT', title: 'Appropriateness questions', desc: 'The most common SJT shape. How to rank options under time pressure.' },
  { id: 'xM8nD3WPUQk', section: 'QR', title: 'Using estimation', desc: 'When to estimate vs calculate — saves 10 seconds on the right questions.' },
  { id: '9yMheB3Ydho', section: 'QR', title: 'Proportion questions', desc: 'Walkthrough of proportion-heavy QR problems.' },
  { id: 'Z53pWxpK27E', section: 'QR', title: 'Interest questions', desc: 'Simple vs compound interest — common QR question type.' },
  { id: 'OmQVvHVxR-Q', section: 'QR', title: 'Ratios', desc: 'Ratios + sharing — UCAT loves these. Drill the pattern.' },
  { id: 'rA7Ya0OJ4Cs', section: 'QR', title: 'Weighted means', desc: 'Tricky averaging questions — formula approach + the shortcut.' },
  { id: '0-LTjomxqis', section: 'QR', title: 'Walkthrough — 830 in QR', desc: 'Kunal scores 830 in QR. Full thought-process walkthrough.' }
];

const sections = ['All', 'VR', 'DM', 'QR', 'SJT'];

export default function UcatVideos() {
  const [filter, setFilter] = useState('All');
  const [active, setActive] = useState(null);

  const visible = useMemo(() => filter === 'All' ? videos : videos.filter((v) => v.section === filter), [filter]);

  return (
    <>
      <div className="page-head">
        <Link to="/practice" className="back-link"><ArrowLeft width="16" height="16" />Back to Practice</Link>
        <h1 style={{ marginTop: 12 }}>UCAT free videos</h1>
        <p className="subtitle">Curated UCAT teaching videos from the Medic Mind YouTube channel. All free.</p>
      </div>

      <div className="card" style={{ marginBottom: 18, padding: '14px 16px' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {sections.map((s) => (
            <button key={s} onClick={() => setFilter(s)} style={pill(filter === s)}>{s}</button>
          ))}
        </div>
      </div>

      <div className="r-grid-2" style={{ marginBottom: 24 }}>
        {visible.map((v) => (
          <article key={v.id} className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: '#000' }}>
              {active === v.id ? (
                <iframe
                  src={`https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0`}
                  title={v.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              ) : (
                <button
                  onClick={() => setActive(v.id)}
                  style={{ position: 'absolute', inset: 0, padding: 0, border: 0, cursor: 'pointer', background: `url(https://i.ytimg.com/vi/${v.id}/hqdefault.jpg) center/cover`, display: 'grid', placeItems: 'center' }}
                  aria-label={`Play ${v.title}`}
                >
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'rgba(35, 20, 63, 0.85)', display: 'grid', placeItems: 'center',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
                  }}>
                    <span style={{ borderLeft: '18px solid #fff', borderTop: '12px solid transparent', borderBottom: '12px solid transparent', marginLeft: 6 }} />
                  </div>
                </button>
              )}
            </div>
            <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{v.section}</span>
                <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none' }}>YouTube ↗</a>
              </div>
              <h3 style={{ margin: '2px 0 0', fontSize: 16 }}>{v.title}</h3>
              <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 13.5, lineHeight: 1.5 }}>{v.desc}</p>
            </div>
          </article>
        ))}
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        Want the full library? Subscribe on the{' '}
        <a href="https://www.youtube.com/@MedicMind/videos" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Medic Mind YouTube channel</a>.
      </p>
    </>
  );
}

function pill(active) {
  return {
    padding: '7px 14px', borderRadius: 99, fontSize: 13, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
    background: active ? 'var(--brand-purple)' : '#fff',
    color: active ? '#fff' : 'var(--text-2)',
    fontWeight: 600
  };
}
